import React, { useEffect } from "react";
import OrderSummary from "./OrderSummary";
import BillSummary from "./BillSummary";
// import { OrderDetails } from "../types/restaurant";
import OrderTypeToggle from "./OrderTypeToggle";
import { usePaymentTheme } from "../../respayment/hooks/usePaymentTheme";
import { CustomerData, Order, OrderedProduct } from "src/shared/services/interfacev2";

interface OrderSectionProps {
  isOrderModified?: boolean;
  isDelivery: boolean;
  setIsDelivery: (value: boolean) => void;
  tableNumber: string;
  guess: string;
  setGuess: (v: string) => void;
  setTableNumber: (v: string) => void;
  oldProducts: Order | undefined;
  selectedProducts: OrderedProduct[];
  editingNote: string | null;
  handleNoteChange: (orderId: string, note: any) => void;
  handleNoteClick: (productId: string) => void;
  handleNoteBlur: (orderId: string) => void;
  deleteProduct: (id: string) => void;
  handleDecrement: (item: OrderedProduct) => void;
  handleIncrement: (item: OrderedProduct) => void;
  calculateSubtotal: () => number;
  discount: number;
  setDiscount: (value: number) => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  calculateTotal: (subtotal: number, isDelivery: boolean) => number;
  handleCancelOrder: (adjust_type: string) => void;
  handleUpdatOrder: () => void;
  resetFormFields: () => void;
  setOpen: (value: boolean) => void;
  open: boolean;
  customers: CustomerData[];
  filteredCustomers: CustomerData[];
  setFilteredCustomers: (customers: CustomerData[]) => void;
  pagenumber: number;
  setPagenumber: (value: number) => void;
  total: { totalRecords: string; totalPage: string };
  sendorder: (customerId: string) => void;
  setCustomerAdd: (value: boolean) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  deliveryFee: number;
  setDeliveryFee: (value: number) => void;
  deliveryFeeEnabled: boolean;
  parcelFee: number;
  setParcelFee: (value: number) => void;
}

const OrderSection: React.FC<OrderSectionProps> = (props) => {
  const subtotal = props.calculateSubtotal();
  const { themeBorderColor, themeTextColor, themeRingColor, getBgClass } = usePaymentTheme();

  useEffect(() => {
    // If cart is empty → reset everything
    if (props.selectedProducts.length === 0) {
      props.setDiscount(0);
      props.setDeliveryFee(0);
      props.setParcelFee(0);
      return;
    }

    // If discount exceeds subtotal → reset discount
    if (props.discount > subtotal) {
      props.setDiscount(0);
    }

    // If delivery fee exceeds subtotal → reset delivery fee
    if (props.deliveryFee > subtotal) {
      props.setDeliveryFee(0);
    }

  }, [props.selectedProducts, subtotal, props.discount, props.deliveryFee, props.parcelFee]);


  return (
    <div className={`w-full xl:w-[40%] lg:w-[45%] md:w-full rounded-xl border shadow-md flex flex-col h-full`}>

      {/* Header */}
      <div className={`${getBgClass()} px-2 py-1 rounded-t-2xl`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
          {props.oldProducts?.orderId && (
            <span className="px-2 py-1 bg-white/20 text-white text-xs sm:text-sm font-medium rounded-full backdrop-blur-sm w-fit">
              Editing #{props.oldProducts.orderId}
            </span>
          )}
        </div>

        <OrderTypeToggle isDelivery={props.isDelivery} setIsDelivery={props.setIsDelivery} orderId={props.oldProducts?.orderId} />
      </div>

      {/* Dine In Section */}
      {!props.isDelivery && (
        <div className="px-2 py-0.5 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">

            {/* Guests */}
            <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
              <label className={`text-xs sm:text-sm font-medium ${themeTextColor}`}>
                Guests
              </label>
              <input
                type="number"
                className={`w-full sm:w-[80px] h-9 border ${themeBorderColor} rounded-lg px-2 text-sm focus:ring-2 ${themeRingColor}`}
                value={props.guess}
                onChange={(e) => props.setGuess(e.target.value)}
                min="1"
                max="20"
              />
            </div>

            {/* Table */}
            <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
              <label className={`text-xs sm:text-sm font-medium ${themeTextColor}`}>
                Table
              </label>
              <input
                type="number"
                className={`w-full sm:w-[80px] h-9 border ${themeBorderColor} rounded-lg px-2 text-sm focus:ring-2 ${themeRingColor}`}
                value={props.tableNumber}
                onChange={(e) => props.setTableNumber(e.target.value)}
                min="1"
                max="50"
              />
            </div>

          </div>
        </div>
      )}

      {/* Order Items */}
      <div className="flex-1 overflow-y-auto">
        <OrderSummary
          selectedProducts={props.selectedProducts}
          editingNote={props.editingNote}
          handleNoteChange={props.handleNoteChange}
          handleNoteClick={props.handleNoteClick}
          handleNoteBlur={props.handleNoteBlur}
          handleIncrement={props.handleIncrement}
          handleDecrement={props.handleDecrement}
          deleteProduct={props.deleteProduct}
        />
      </div>

      {/* Bill Summary */}
      <div className="border-t bg-white sticky bottom-0">
        <BillSummary
          subtotal={subtotal}
          discount={props.discount}
          setDiscount={props.setDiscount}
          calculateTotal={props.calculateTotal}
          handleCancelOrder={props.handleCancelOrder}
          handleUpdatOrder={props.handleUpdatOrder}
          open={props.open}
          setOpen={props.setOpen}
          customers={props.customers}
          filteredCustomers={props.filteredCustomers}
          setFilteredCustomers={props.setFilteredCustomers}
          sendorder={props.sendorder}
          pagenumber={props.pagenumber}
          setPagenumber={props.setPagenumber}
          total={props.total}
          setCustomerAdd={props.setCustomerAdd}
          setIsEditing={props.setIsEditing}
          isOrderModified={props.isOrderModified}
          selectedProducts={props.selectedProducts}
          isEditing={props.isEditing}
          searchTerm={props.searchTerm}
          setSearchTerm={props.setSearchTerm}
          resetFormFields={props.resetFormFields}
          deliveryFee={props.deliveryFee}
          setDeliveryFee={props.setDeliveryFee}
          deliveryFeeEnabled={props.isDelivery ? props.deliveryFeeEnabled : false}
          isDelivery={props.isDelivery}
          parcelFee={props.parcelFee}
          setParcelFee={props.setParcelFee}
        />
      </div>

    </div>
  );
};

export default OrderSection;
